import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, BadgeCheck, Loader2, Mail, MapPin, Phone, Target, UserPlus } from "lucide-react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useApp } from "@/context/AppContext";
import { createAdminSalesAgent } from "@/lib/api";

const initialForm = {
  fullName: "",
  email: "",
  phone: "",
  territory: "",
  dailyTarget: "3",
};

export default function CreateSalesAgent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentAdmin } = useApp();
  const [form, setForm] = useState(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const setField = (field) => (event) => setForm((current) => ({
    ...current,
    [field]: event.target.value,
  }));
  const errors = {
    fullName: form.fullName.trim().length >= 2 ? "" : "Enter the agent’s full name.",
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()) ? "" : "Enter a valid email address.",
    phone: /^\+[1-9][0-9]{7,14}$/.test(form.phone.replace(/\s/g, "")) ? "" : "Use international format, for example +2348012345678.",
    territory: form.territory.trim().length >= 2 ? "" : "Enter the agent’s assigned territory.",
    dailyTarget: Number(form.dailyTarget) >= 1 && Number(form.dailyTarget) <= 100 ? "" : "Target must be between 1 and 100.",
  };
  const isValid = Object.values(errors).every((error) => !error);
  const createAgent = useMutation({
    mutationFn: () => createAdminSalesAgent({
      fullName: form.fullName.trim(),
      email: form.email.trim().toLowerCase(),
      phone: form.phone.replace(/\s/g, ""),
      territory: form.territory.trim(),
      dailyTarget: Number(form.dailyTarget),
    }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["admin-sales-agents"] });
      toast.success(`${result.agent.fullName} was added to the field team.`);
      navigate("/sales", { replace: true });
    },
    onError: (error) => toast.error(error?.message || "Could not create the sales agent."),
  });
  const submit = (event) => {
    event.preventDefault();
    setSubmitted(true);
    if (isValid && !createAgent.isPending) createAgent.mutate();
  };
  if (!currentAdmin?.permissions?.includes("sales.manage")) {
    return <Navigate to="/sales" replace />;
  }

  return (
    <div className="flex min-h-[calc(100vh-9rem)] items-center justify-center py-6" data-testid="create-sales-agent-page">
      <div className="w-full max-w-4xl overflow-hidden rounded-2xl border border-[#0E5B52]/10 bg-white shadow-[0_18px_45px_rgba(14,91,82,0.08)]">
        <div className="grid lg:grid-cols-[0.78fr_1.22fr]">
          <aside className="relative overflow-hidden bg-[#0E5B52] p-8 text-white">
            <div className="absolute -right-14 -top-14 h-44 w-44 rounded-full border border-white/10" />
            <div className="absolute -bottom-20 -left-16 h-56 w-56 rounded-full bg-[#FDE047]/10" />
            <Link to="/sales" className="relative inline-flex items-center gap-2 text-sm text-white/75 transition hover:text-white">
              <ArrowLeft className="h-4 w-4" /> Back to sales pipeline
            </Link>
            <div className="relative mt-16">
              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10">
                <UserPlus className="h-6 w-6 text-[#FDE047]" />
              </div>
              <div className="text-xs font-medium uppercase tracking-[0.18em] text-[#A7F3D0]">Field team</div>
              <h1 className="mt-2 font-display text-3xl font-semibold leading-tight">Create a sales agent</h1>
              <p className="mt-4 max-w-xs text-sm leading-6 text-white/70">
                Set up an accountable territory owner with a secure, automatically generated referral code.
              </p>
              <div className="mt-10 space-y-3 text-sm text-white/80">
                <Benefit icon={BadgeCheck} text="Unique referral attribution" />
                <Benefit icon={MapPin} text="Clear territory ownership" />
                <Benefit icon={Target} text="Measurable daily targets" />
              </div>
            </div>
          </aside>

          <form onSubmit={submit} noValidate className="p-7 sm:p-10">
            <div className="mb-8">
              <div className="text-xs font-medium uppercase tracking-[0.14em] text-[#0E5B52]/70">Agent profile</div>
              <h2 className="mt-2 font-display text-2xl font-semibold text-[#0F2A26]">Field assignment details</h2>
              <p className="mt-2 text-sm leading-6 text-[#6B7280]">
                Contact details are used for operations and must belong to the assigned agent.
              </p>
            </div>

            <div className="space-y-5">
              <Field label="Full name" icon={UserPlus} error={submitted && errors.fullName}>
                <Input autoFocus autoComplete="name" maxLength={120} value={form.fullName} onChange={setField("fullName")} placeholder="e.g. Amina Bello" aria-invalid={Boolean(submitted && errors.fullName)} />
              </Field>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Email address" icon={Mail} error={submitted && errors.email}>
                  <Input type="email" autoComplete="email" maxLength={254} value={form.email} onChange={setField("email")} placeholder="amina@example.com" aria-invalid={Boolean(submitted && errors.email)} />
                </Field>
                <Field label="Phone number" icon={Phone} error={submitted && errors.phone}>
                  <Input type="tel" autoComplete="tel" maxLength={16} value={form.phone} onChange={setField("phone")} placeholder="+2348012345678" aria-invalid={Boolean(submitted && errors.phone)} />
                </Field>
              </div>
              <div className="grid gap-5 sm:grid-cols-[1fr_0.48fr]">
                <Field label="Assigned territory" icon={MapPin} error={submitted && errors.territory}>
                  <Input maxLength={100} value={form.territory} onChange={setField("territory")} placeholder="e.g. Lagos Mainland" aria-invalid={Boolean(submitted && errors.territory)} />
                </Field>
                <Field label="Daily target" icon={Target} error={submitted && errors.dailyTarget}>
                  <Input type="number" inputMode="numeric" min="1" max="100" value={form.dailyTarget} onChange={setField("dailyTarget")} aria-invalid={Boolean(submitted && errors.dailyTarget)} />
                </Field>
              </div>
            </div>

            <div className="mt-8 rounded-xl bg-[#F4F9F5] px-4 py-3 text-xs leading-5 text-[#4B5563]">
              A unique agent referral code is created automatically. The action is recorded in the administrative audit log.
            </div>
            <div className="mt-7 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button asChild type="button" variant="outline"><Link to="/sales">Cancel</Link></Button>
              <Button type="submit" disabled={createAgent.isPending} className="min-w-40 bg-[#0E5B52] hover:bg-[#0B4A43]">
                {createAgent.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />}
                Create sales agent
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, icon: Icon, error, children }) {
  return (
    <div>
      <Label className="mb-2 flex items-center gap-2 text-sm text-[#0F2A26]"><Icon className="h-4 w-4 text-[#0E5B52]" />{label}</Label>
      {children}
      {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
    </div>
  );
}

function Benefit({ icon: Icon, text }) {
  return <div className="flex items-center gap-3"><span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10"><Icon className="h-4 w-4 text-[#A7F3D0]" /></span>{text}</div>;
}
